import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StudentProfileViewer.css';
import {
  ArrowLeft,
  User,
  MapPin,
  GraduationCap,
  Briefcase,
  FileText,
  Linkedin,
  Download,
  Globe,
  Phone,
  Mail,
  Building2,
  Calendar,
  Award,
  Clock,
  DollarSign,
  Loader2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const StudentProfileViewer = ({ studentId, onBack }) => {
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchStudentData = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/candidate/profile/${studentId}`
        );
        setStudentData(response.data);
      } catch (err) {
        console.error('Failed to fetch student profile:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    if (studentId) {
      fetchStudentData();
    } else {
      setIsLoading(false);
    }
  }, [studentId]);

  const formatBoolean = (value) =>
    value === true ? 'Yes' : value === false ? 'No' : 'N/A';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="spv-root">
        <div className="spv-loading">
          <div className="spv-loading-card">
            <Loader2 className="spv-spin" size={36} />
            <span>Loading student profile…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error / Not Found ── */
  if (error || !studentData) {
    return (
      <div className="spv-root">
        <div className="spv-loading">
          <div className="spv-loading-card spv-error-card">
            <AlertCircle size={40} className="spv-error-icon" />
            <span>Student profile not found or failed to load.</span>
            <button className="spv-back-btn" onClick={onBack}>
              <ArrowLeft size={16} /> Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── InfoRow Component ── */
  const InfoRow = ({ icon: Icon, label, value, isLink, href, fullWidth }) => (
    <div className={`spv-info-row ${fullWidth ? 'spv-full-width' : ''}`}>
      <div className="spv-info-label">
        {Icon && <Icon size={14} className="spv-info-label-icon" />}
        {label}
      </div>
      <div className="spv-info-value">
        {isLink && value && value !== 'N/A' ? (
          <a
            href={href || (value.startsWith('http') ? value : `https://${value}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="spv-link"
          >
            {label === 'Resume' ? (
              <>
                <Download size={13} /> Download Resume
              </>
            ) : (
              <>
                <ExternalLink size={13} /> {value.length > 40 ? value.substring(0, 40) + '…' : value}
              </>
            )}
          </a>
        ) : (
          <span className={!value || value === 'N/A' ? 'spv-dim' : ''}>
            {value || 'N/A'}
          </span>
        )}
      </div>
    </div>
  );

  /* ── StatusBadge ── */
  const StatusBadge = ({ value, yesColor = 'green', noColor = 'neutral' }) => {
    const isYes = value === true;
    const colorClass = isYes ? yesColor : noColor;
    return (
      <span className={`spv-status-badge spv-badge-${colorClass}`}>
        {isYes ? 'Yes' : 'No'}
      </span>
    );
  };

  return (
    <div className="spv-root">
      {/* Background blobs */}
      <div className="spv-bg-blob spv-blob-1" />
      <div className="spv-bg-blob spv-blob-2" />

      {/* ── Back Button ── */}
      <button className="spv-back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> Back to Roster
      </button>

      {/* ── Profile Hero Card ── */}
      <div className="spv-hero-card">
        <div className="spv-hero-left">
          <div className="spv-hero-avatar">{getInitials(studentData.fullName)}</div>
          <div className="spv-hero-info">
            <h1 className="spv-hero-name">{studentData.fullName}</h1>
            <div className="spv-hero-meta">
              {studentData.rollNo && (
                <span className="spv-hero-tag">
                  <GraduationCap size={13} /> {studentData.rollNo}
                </span>
              )}
              {studentData.collegeName && (
                <span className="spv-hero-tag">
                  <Building2 size={13} /> {studentData.collegeName}
                </span>
              )}
            </div>
            <div className="spv-hero-contact">
              {studentData.personalEmail && (
                <span className="spv-contact-item">
                  <Mail size={13} /> {studentData.personalEmail}
                </span>
              )}
              {studentData.phoneNumber && (
                <span className="spv-contact-item">
                  <Phone size={13} /> {studentData.phoneNumber}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="spv-hero-stats">
          {studentData.currentCGPA && (
            <div className="spv-stat-card">
              <span className="spv-stat-value">{studentData.currentCGPA}</span>
              <span className="spv-stat-label">CGPA</span>
            </div>
          )}
          {studentData.totalExperience && (
            <div className="spv-stat-card">
              <span className="spv-stat-value">{studentData.totalExperience}</span>
              <span className="spv-stat-label">Yrs Exp</span>
            </div>
          )}
          {studentData.expectedCTC && (
            <div className="spv-stat-card">
              <span className="spv-stat-value">{studentData.expectedCTC}</span>
              <span className="spv-stat-label">Expected CTC</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Sections Grid ── */}
      <div className="spv-sections-grid">

        {/* Section: Personal Information */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon"><User size={18} /></div>
            <h3>Personal Information</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow icon={User} label="Full Name" value={studentData.fullName} />
            <InfoRow icon={Mail} label="Email" value={studentData.personalEmail} />
            <InfoRow icon={Phone} label="Phone" value={studentData.phoneNumber} />
            <InfoRow icon={Globe} label="Citizenship" value={studentData.citizenship} />
          </div>
        </div>

        {/* Section: Permanent Address */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon spv-icon-teal"><MapPin size={18} /></div>
            <h3>Permanent Address</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow icon={MapPin} label="Street" value={studentData.permanentStreet} />
            <InfoRow
              icon={Building2}
              label="City, State"
              value={`${studentData.permanentCity || 'N/A'}, ${studentData.permanentState || 'N/A'}`}
            />
            <InfoRow label="Pincode" value={studentData.permanentPincode} />
            <InfoRow icon={Globe} label="Country" value={studentData.permanentCountry} />
          </div>
        </div>

        {/* Section: College Address */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon spv-icon-amber"><Building2 size={18} /></div>
            <h3>College Address</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow icon={MapPin} label="Street" value={studentData.collegeStreet} />
            <InfoRow
              icon={Building2}
              label="City, State"
              value={`${studentData.collegeCity || 'N/A'}, ${studentData.collegeState || 'N/A'}`}
            />
            <InfoRow label="Pincode" value={studentData.collegePincode} />
            <InfoRow icon={Globe} label="Country" value={studentData.collegeCountry} />
          </div>
        </div>

        {/* Section: Educational Information */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon spv-icon-purple"><GraduationCap size={18} /></div>
            <h3>Educational Information</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow icon={GraduationCap} label="Roll No" value={studentData.rollNo} />
            <InfoRow icon={Building2} label="College" value={studentData.collegeName} />
            <InfoRow icon={Building2} label="University" value={studentData.universityName} />
            <InfoRow icon={Award} label="Current CGPA" value={studentData.currentCGPA} />
            <InfoRow label="10th % / CGPA" value={studentData.tenthPercentile} />
            <InfoRow label="10th Board" value={studentData.tenthBoard} />
            <InfoRow label="12th % / CGPA" value={studentData.twelfthPercentile} />
            <InfoRow label="12th Board" value={studentData.twelfthBoard} />
            <InfoRow label="12th Stream" value={studentData.twelfthStream} />
          </div>
        </div>

        {/* Section: Experience & Compensation */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon spv-icon-green"><Briefcase size={18} /></div>
            <h3>Experience & Compensation</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow icon={Clock} label="Total Experience" value={`${studentData.totalExperience || '0'} yrs`} />
            <InfoRow icon={Clock} label="Relevant Experience" value={`${studentData.relevantExperience || '0'} yrs`} />
            <InfoRow icon={DollarSign} label="Current CTC (LPA)" value={studentData.currentCTC} />
            <InfoRow icon={DollarSign} label="Expected CTC (LPA)" value={studentData.expectedCTC} />
          </div>
        </div>

        {/* Section: Job Status & Documents */}
        <div className="spv-section-card">
          <div className="spv-section-header">
            <div className="spv-section-icon spv-icon-rose"><FileText size={18} /></div>
            <h3>Job Status & Documents</h3>
          </div>
          <div className="spv-info-list">
            <InfoRow label="Skills" value={studentData.skills} fullWidth />
            <InfoRow icon={Clock} label="Notice Period" value={studentData.noticePeriod} />

            <div className="spv-info-row">
              <div className="spv-info-label">Serving Notice</div>
              <div className="spv-info-value">
                <StatusBadge value={studentData.isServingNotice} yesColor="amber" />
              </div>
            </div>

            {studentData.isServingNotice && (
              <InfoRow
                icon={Calendar}
                label="Last Working Day"
                value={formatDate(studentData.lastWorkingDay)}
              />
            )}

            <div className="spv-info-row">
              <div className="spv-info-label">Abroad Opportunities</div>
              <div className="spv-info-value">
                <StatusBadge value={studentData.isAbroadOpportunity} yesColor="green" />
              </div>
            </div>

            {studentData.isAbroadOpportunity && (
              <InfoRow label="Passport No" value={studentData.passportNumber} />
            )}

            <InfoRow
              icon={Linkedin}
              label="LinkedIn"
              value={studentData.linkedinURL}
              isLink={!!studentData.linkedinURL}
            />

            <InfoRow
              icon={FileText}
              label="Resume"
              value={studentData.resumeFile ? 'Download' : 'Not Uploaded'}
              isLink={!!studentData.resumeFile}
              // 🚨 THE FIX: Just use the raw URL exactly as it comes from the backend! 🚨
              href={studentData.resumeFile || undefined} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileViewer;